import * as React from 'react';
import { useMemo, useState } from 'react';
import { formatNumber } from '../../utils/currency';
import moment from 'moment';

export interface RevenuePoint {
  date: string;
  amount: number;
}

interface RevenueChartProps {
  series: RevenuePoint[];
  currency: string;
}

const VIEW_W = 800;
const VIEW_H = 220;
const PAD_X = 8;
const PAD_TOP = 12;
const PAD_BOTTOM = 8;

/**
 * Lightweight, dependency-free revenue chart. Renders the daily revenue series
 * as an SVG area + line that scales responsively via viewBox. Uses Polaris CSS
 * custom properties so it tracks the merchant's light/dark theme.
 */
const RevenueChart: React.FC<RevenueChartProps> = ({ series, currency }) => {
  const [hover, setHover] = useState<number | null>(null);

  const { areaPath, linePath, points, max } = useMemo(() => {
    const max = Math.max(1, ...series.map((p) => p.amount));
    const innerW = VIEW_W - PAD_X * 2;
    const innerH = VIEW_H - PAD_TOP - PAD_BOTTOM;
    const step = series.length > 1 ? innerW / (series.length - 1) : 0;

    const points = series.map((p, i) => {
      const x = PAD_X + step * i;
      const y = PAD_TOP + innerH - (p.amount / max) * innerH;
      return { x, y, ...p };
    });

    if (points.length === 0) {
      return { areaPath: '', linePath: '', points, max };
    }

    const linePath = points
      .map((pt, i) => `${i === 0 ? 'M' : 'L'} ${pt.x.toFixed(2)} ${pt.y.toFixed(2)}`)
      .join(' ');

    const baseline = PAD_TOP + innerH;
    const areaPath =
      `M ${points[0].x.toFixed(2)} ${baseline.toFixed(2)} ` +
      points.map((pt) => `L ${pt.x.toFixed(2)} ${pt.y.toFixed(2)}`).join(' ') +
      ` L ${points[points.length - 1].x.toFixed(2)} ${baseline.toFixed(2)} Z`;

    return { areaPath, linePath, points, max };
  }, [series]);

  // An all-zero series (a shop with no sales in the window) would otherwise render a full-height
  // chart that is nothing but a flat line pinned to the baseline — a big empty box. Collapse it to
  // the same compact empty state as a missing series so the card sizes to its content.
  const hasRevenue = series.some((p) => p.amount > 0);
  if (series.length === 0 || !hasRevenue) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <s-text color="subdued">No revenue data for this period yet.</s-text>
      </div>
    );
  }

  const accent = '#6366f1';
  const active = hover != null ? points[hover] : null;

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="none"
        style={{ width: '100%', height: 220, display: 'block', overflow: 'visible' }}
        role="img"
        aria-label="Revenue over time"
      >
        <defs>
          <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={accent} stopOpacity="0.28" />
            <stop offset="100%" stopColor={accent} stopOpacity="0" />
          </linearGradient>
        </defs>

        {areaPath && <path d={areaPath} fill="url(#revFill)" stroke="none" />}
        {linePath && (
          <path d={linePath} fill="none" stroke={accent} strokeWidth={2} vectorEffect="non-scaling-stroke" />
        )}

        {active && (
          <line
            x1={active.x}
            y1={PAD_TOP}
            x2={active.x}
            y2={VIEW_H - PAD_BOTTOM}
            stroke={accent}
            strokeWidth={1}
            strokeDasharray="3 3"
            vectorEffect="non-scaling-stroke"
            opacity={0.5}
          />
        )}
        {active && <circle cx={active.x} cy={active.y} r={4} fill={accent} />}

        {/* Invisible hover targets — one column per day */}
        {points.map((pt, i) => {
          const half = points.length > 1 ? (VIEW_W - PAD_X * 2) / (points.length - 1) / 2 : VIEW_W;
          return (
            <rect
              key={pt.date}
              x={pt.x - half}
              y={0}
              width={half * 2}
              height={VIEW_H}
              fill="transparent"
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover((h) => (h === i ? null : h))}
            />
          );
        })}
      </svg>

      {active && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: `${(active.x / VIEW_W) * 100}%`,
            transform: 'translateX(-50%)',
            pointerEvents: 'none',
            background: 'var(--p-color-bg-surface, #ffffff)',
            color: 'var(--p-color-text, #303030)',
            border: '1px solid var(--p-color-border, #e3e3e3)',
            borderRadius: 8,
            padding: '4px 8px',
            fontSize: 12,
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
        >
          <strong>{formatNumber(active.amount, currency)}</strong>
          <span style={{ opacity: 0.7 }}> · {moment(active.date).format('MMM D')}</span>
        </div>
      )}
    </div>
  );
};

export default RevenueChart;
